import * as Sentry from '@sentry/nextjs'
import { type Auth, type User, browserLocalPersistence, initializeAuth } from 'firebase/auth'
import React, { createContext, useEffect, useMemo, useState } from 'react'

import firebaseApp from '@/lib/firebase/firebaseConfig'

type AuthState =
  | {
      auth: Auth
      user: User | null
      isLoading: false
    }
  | {
      auth: undefined
      user: undefined
      isLoading: true
    }

interface IAuthContext {
  authState: AuthState
}

export const AuthContext = createContext<IAuthContext>({
  authState: {
    auth: undefined,
    isLoading: true,
    user: undefined,
  },
})

interface AuthContextProviderProps {
  children: React.ReactNode
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const firebaseAuth = useMemo(() => {
    try {
      return initializeAuth(firebaseApp, { persistence: browserLocalPersistence })
    } catch (error) {
      Sentry.captureException(new Error('initialiseAuth error', { cause: error }))
      return undefined
    }
  }, [])

  const [authState, setAuthState] = useState<AuthState>({
    auth: undefined,
    isLoading: true,
    user: undefined,
  })

  useEffect(() => {
    if (!firebaseAuth) {
      return
    }
    const unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      setAuthState({
        auth: firebaseAuth,
        isLoading: false,
        user,
      })
    })

    return () => unsubscribe()
  }, [firebaseAuth])

  return <AuthContext.Provider value={{ authState }}>{children}</AuthContext.Provider>
}
